import { createEventOrder, createUserEvent } from "../api";
import { getRandomUserAvatarByGender } from "../utils/userAvatar";
import { getTicketOriginalPrice } from "../utils/prices.js";
import checkoutStore from "../stores/checkout.store";


export const UserEventService = {
    prepareUserEvents(userEventFormData, summary, eventInputData, coupon, tickets, seasonTicket, timeSlotId, timeSlotDuration) {
        const { event } = checkoutStore
        const allUserEvents = userEventFormData.map((userEventData, i) => {

            const { fname, lname, phone, email, gender, birth_date, social_link, ticketId, ticketInfo, isAllowMarketingDelivery, businessCode, businessName } = userEventData
            const ticketOriginalPrice = getTicketOriginalPrice(tickets, ticketId, coupon)
            const userEvent = {
                fname,
                lname,
                gender,
                phone,
                birth_date,
                email: email.toLowerCase().trim(),
                isAllowMarketingDelivery,
                businessCode: businessCode || '',
                businessName: businessName || '',
                ticketsInfo: [{
                    isApproved: false,
                    isCheckedIn: false,
                    isRefunded: false,
                    originalPrice: ticketOriginalPrice,
                    ticket: ticketId,
                }]
            }

            // Add custom questions answers
            for (const eventInput of eventInputData) {
                if (eventInput.name && eventInput.name.startsWith('custom_q')) {
                    if (!userEvent.custom_q) {
                        userEvent.custom_q = {}
                    }
                    if (userEventData[eventInput.name]) {
                        userEvent.custom_q[eventInput.name] = userEventData[eventInput.name]
                    }
                }
            }

            // If we have seats, then create ticket per chosen seat (meaning multiple tickets per userEvent when choosing seats)
            if (event.isSeatsEvent) {
                userEvent.ticketsInfo = ticketInfo.seats.map(seat => {
                    const ticketId = seat.nimiTicketId ? seat.nimiTicketId : ticketId
                    const ticketOriginalPrice = getTicketOriginalPrice(tickets, ticketId, coupon)

                    return {
                        isApproved: false,
                        isCheckedIn: false,
                        isRefunded: false,
                        ticket: ticketId,
                        originalPrice: ticketOriginalPrice,
                        seat: seat.id,
                        categoryLabel: seat.categoryLabel,
                        rowLabel: seat.rowLabel,
                        seatLabel: seat.seatLabel,
                    }
                })
            } else if (event.isOneUserFormPerOrder) { // If we have one user form per order, then we need to create one userEvent with all tickets
                userEvent.ticketsInfo = summary.map(ticket => {

                    const ticketOriginalPrice = getTicketOriginalPrice(tickets, ticket.ticketId, coupon)

                    return {
                        isApproved: false,
                        isCheckedIn: false,
                        isRefunded: false,
                        originalPrice: ticketOriginalPrice,
                        ticket: ticket.ticketId,
                    }
                })
            }

            const normalizedSocialLink = social_link.toLowerCase().trim()

            if (normalizedSocialLink.includes('facebook')) {
                userEvent.fbProfile = normalizedSocialLink
            }

            if (normalizedSocialLink.includes('instagram')) {
                userEvent.instagramProfile = normalizedSocialLink
            }

            userEvent.social_link = normalizedSocialLink

            userEvent.image_url = getRandomUserAvatarByGender(gender)

            // Add time slot information if provided
            if (timeSlotId) {
                userEvent.timeSlotId = timeSlotId
                // Parse the timeSlotId to extract date and time
                const [date, time] = timeSlotId.split('_')
                if (date && time) {
                    userEvent.timeSlotDate = date
                    userEvent.timeSlotTime = time
                }
                // Add duration if provided
                if (timeSlotDuration) {
                    userEvent.timeSlotDuration = timeSlotDuration
                }
            }

            // In case of 'seasonTicket' (מנוי) we can apply many tickets for one userEvent, hence we use the ticketInfo property just like the seats logic.
            // We also make sure it will happen only once with the index check 'i === 0', it won't suppose to happen more than once but still
            // if (seasonTicket && i === 0) {
            //     userEvent.ticketsInfo = summary.map(ticketSummary => {
            //         return {
            //             isApproved: false,
            //             isCheckedIn: false,
            //             isRefunded: false,
            //             originalPrice: getTicketOriginalPrice(tickets, ticketSummary.ticketId, coupon),
            //             ticket: ticketSummary.ticketId,
            //         }
            //     })
            // }

            return { userEvent, ticketId }
        })

        return { allUserEvents, primary: allUserEvents[0] }
    },
    async createManyUserEvents({ accountId, eventId, allUserEvents }) {
        const promises = allUserEvents.map(({ userEvent, ticketId }, index) => {
            userEvent.isOrderCreator = index === 0
            return createUserEvent({ accountId, eventId, ticketId, userEvent })
        })
        const results = await Promise.all(promises)
        return results.map(singleResults => singleResults.data)
    },

    async createEventOrder({ accountId, eventId, purchaseToken, userEvents, coupon, utm, seasonTicket, seller, marketplaceId, timeSlotId, timeSlotDuration, eventLinkName, paymentProvider, externalCustomerId, createdByUserId, createdByUserName }) {
        const eventOrder = { userEvents: userEvents.map(({ _id }) => _id), purchaseToken, seller }

        // Include payment provider information
        if (paymentProvider) {
            eventOrder.paymentProvider = paymentProvider
        }

        // Include Stripe customer ID if provided (cus_xxx)
        if (externalCustomerId) {
            eventOrder.externalCustomerId = externalCustomerId
        }

        if (coupon) {
            eventOrder.coupon = coupon._id
            eventOrder.couponOriginalAmount = coupon.amount
            eventOrder.couponOriginalType = coupon.type
        }

        if (utm) {
            eventOrder.utm = utm
        }

        if (marketplaceId) {
            eventOrder.marketplace = marketplaceId
        }

        if (seasonTicket) {
            const { endUserSeasonTicket, endUser } = seasonTicket
            eventOrder.endUserSeasonTicket = {
                _id: endUserSeasonTicket._id,
                endUser: endUser._id,
                name: endUserSeasonTicket.name,
                seasonTicket: endUserSeasonTicket.seasonTicket,
                hrc: endUserSeasonTicket.hrc,
            }
        }

        // Add time slot information if provided
        if (timeSlotId) {
            eventOrder.timeSlotId = timeSlotId
            // Parse the timeSlotId to extract date and time for easier querying
            // Format: "2025-09-23_1300"
            const [date, time] = timeSlotId.split('_')
            if (date && time) {
                eventOrder.timeSlotDate = date
                eventOrder.timeSlotTime = time
            }
            // Add duration if provided
            if (timeSlotDuration) {
                eventOrder.timeSlotDuration = timeSlotDuration
            }
        }

        // Add UTM link name if provided
        if(eventLinkName) {
            eventOrder.eventLinkName = eventLinkName
        }

        if (createdByUserId) {
            eventOrder.createdByUserId = createdByUserId
        }

        const normalizedCreatedByUserName = createdByUserName?.trim()
        if (normalizedCreatedByUserName) {
            eventOrder.createdByUserName = normalizedCreatedByUserName
        }

        return createEventOrder({ accountId, eventId, eventOrder })
    }

}
